import { useNavigate } from 'react-router-dom'

export function Privacy() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      <nav
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #f3f4f6',
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 0',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <img
                src="/favicon.png"
                alt="Little Sister Logo"
                style={{ height: '32px', width: '32px', objectFit: 'contain' }}
              />
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a5f' }}>
                Little Sister
              </span>
            </div>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 1rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1e3a5f', marginBottom: '0.5rem' }}>
          Privacy Policy
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '0.25rem' }}>
          <strong>Little Sister — Medication Reminder Service</strong>
        </p>
        <p style={{ color: '#6b7280', marginBottom: '0.25rem' }}>
          <strong>Last Updated:</strong> March 27, 2026
        </p>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          <strong>Effective Date:</strong> March 27, 2026
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <Section title="1. Introduction">
            <p>
              Little Sister ("we," "us," "our," or the "Service") is a medication reminder and
              caregiver coordination platform. We use artificial intelligence to place automated
              phone calls to elderly loved ones, reminding them to take their medications, and we
              report outcomes to designated caregivers.
            </p>
            <p>
              Because we handle sensitive health-related information, we take your privacy
              seriously. This Privacy Policy explains what data we collect, why we collect it, how
              we use and share it, and what rights you have regarding your information.
            </p>
            <p>
              This policy applies to all users of the Service, including <strong>caregivers</strong>{' '}
              (family members or other persons who manage medication schedules) and{' '}
              <strong>loved ones</strong> (the individuals receiving reminder calls).
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <h4 style={subheadingStyle}>2.1 Information You Provide Directly</h4>
            <p>
              <strong>Caregiver Account Information:</strong>
            </p>
            <ul style={listStyle}>
              <li>Full name</li>
              <li>Email address (via Auth0 login)</li>
              <li>Phone number</li>
              <li>Notification preferences (separate email and/or phone for alerts)</li>
              <li>Language preference (English or Traditional Chinese)</li>
              <li>Timezone</li>
              <li>Profile photo (optional)</li>
            </ul>
            <p>
              <strong>Loved One Information:</strong>
            </p>
            <ul style={listStyle}>
              <li>Full name</li>
              <li>Phone number</li>
              <li>Email address (optional)</li>
              <li>Language preference</li>
              <li>Timezone</li>
              <li>Profile photo (optional)</li>
            </ul>
            <p>
              <strong>Medication Information:</strong>
            </p>
            <ul style={listStyle}>
              <li>Medication names</li>
              <li>Dosage schedules and times</li>
              <li>Frequency (daily, weekly, biweekly, monthly, or one-time)</li>
              <li>Notes (e.g., "take with food")</li>
              <li>Vacation pause dates (periods when reminders are suspended)</li>
            </ul>
            <p>
              <strong>Relationship Information:</strong>
            </p>
            <ul style={listStyle}>
              <li>Caregiver-to-loved-one relationship type</li>
              <li>Permission levels (primary or secondary caregiver)</li>
              <li>Access rights (view-only or full access)</li>
            </ul>

            <h4 style={subheadingStyle}>2.2 Information Collected During Phone Calls</h4>
            <p>
              When our AI assistant calls a loved one or when a caregiver completes voice
              onboarding, we collect:
            </p>
            <ul style={listStyle}>
              <li>
                <strong>Full conversation transcripts</strong> — a text record of what is spoken by
                both the AI assistant and the human participant
              </li>
              <li>
                <strong>Call metadata</strong> — call duration, start and end times, call status
                (answered, missed, failed)
              </li>
              <li>
                <strong>Call outcomes</strong> — whether the loved one confirmed taking medication,
                declined, or did not answer
              </li>
              <li>
                <strong>AI-generated call summaries</strong> — brief notes produced by the AI about
                the call
              </li>
              <li>
                <strong>Verbal consent records</strong> — if a loved one verbally consents to
                receiving reminder calls, we record the relevant transcript excerpt (up to 500
                characters) as proof of consent
              </li>
            </ul>

            <h4 style={subheadingStyle}>2.3 Information Collected Automatically</h4>
            <ul style={listStyle}>
              <li>
                <strong>IP addresses</strong> — recorded when you sign up, give consent, or access
                the Service (for audit and security purposes)
              </li>
              <li>
                <strong>Authentication data</strong> — Auth0 identity tokens used to verify your
                account
              </li>
              <li>
                <strong>Timestamps</strong> — records of when actions are taken (logins, data
                changes, consent events)
              </li>
            </ul>

            <h4 style={subheadingStyle}>2.4 Information We Do NOT Collect</h4>
            <ul style={listStyle}>
              <li>
                We do not store raw audio recordings of phone calls; we store text transcripts only
              </li>
              <li>We do not collect financial or payment information at this time</li>
              <li>
                We do not collect precise geolocation data (we use your timezone for scheduling
                only)
              </li>
              <li>We do not collect data from individuals under the age of 18</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use the information we collect for the following purposes:</p>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Purpose</th>
                  <th style={thStyle}>Data Used</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>Placing medication reminder calls</td>
                  <td style={tdStyle}>
                    Loved one's name, phone number, medication list, schedule, language preference
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Notifying caregivers of outcomes</td>
                  <td style={tdStyle}>
                    Caregiver phone/email, call outcomes, loved one's name, medication names
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Providing the caregiver dashboard</td>
                  <td style={tdStyle}>All account, medication, and call history data</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Voice onboarding</td>
                  <td style={tdStyle}>
                    Names, phone numbers, medications, preferences collected during call
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Personalizing the AI assistant</td>
                  <td style={tdStyle}>
                    Loved one's name, caregiver's name, medication details, language preference
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Maintaining consent records</td>
                  <td style={tdStyle}>
                    Consent status, timestamps, IP addresses, transcript excerpts
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Audit and compliance</td>
                  <td style={tdStyle}>IP addresses, timestamps, action logs, consent records</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Improving the Service</td>
                  <td style={tdStyle}>Aggregated, de-identified usage patterns</td>
                </tr>
              </tbody>
            </table>
            <p>
              We do <strong>not</strong> use your information to:
            </p>
            <ul style={listStyle}>
              <li>Sell data to third parties</li>
              <li>Display advertisements</li>
              <li>Build marketing profiles</li>
              <li>Make automated decisions about your healthcare</li>
            </ul>
          </Section>

          <Section title="4. How We Share Your Information">
            <p>
              We share your information only with the third-party service providers necessary to
              operate the Service. We do not sell, rent, or trade your personal information.
            </p>

            <h4 style={subheadingStyle}>4.1 Service Providers</h4>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Provider</th>
                  <th style={thStyle}>What They Receive</th>
                  <th style={thStyle}>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>Twilio</td>
                  <td style={tdStyle}>Phone numbers, voice audio during calls</td>
                  <td style={tdStyle}>
                    Placing and receiving phone calls; sending SMS notifications to caregivers
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Anthropic (Claude AI)</td>
                  <td style={tdStyle}>
                    Names, medication details, conversation history (per call turn)
                  </td>
                  <td style={tdStyle}>Powering the AI assistant that conducts reminder calls</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Deepgram</td>
                  <td style={tdStyle}>Voice audio stream (during calls only)</td>
                  <td style={tdStyle}>Converting speech to text in real time</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Cartesia</td>
                  <td style={tdStyle}>Text of AI responses</td>
                  <td style={tdStyle}>Converting AI text responses to spoken audio</td>
                </tr>
                <tr>
                  <td style={tdStyle}>LiveKit</td>
                  <td style={tdStyle}>Audio streams, session metadata</td>
                  <td style={tdStyle}>Managing real-time voice communication</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Auth0</td>
                  <td style={tdStyle}>Email address</td>
                  <td style={tdStyle}>User authentication and login</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Convex</td>
                  <td style={tdStyle}>All stored data</td>
                  <td style={tdStyle}>Cloud database and backend infrastructure</td>
                </tr>
              </tbody>
            </table>
            <p>
              <strong>Important notes about third-party data handling:</strong>
            </p>
            <ul style={listStyle}>
              <li>
                Anthropic (Claude) processes conversation data per-turn during calls but does not
                retain it for model training under our API agreement
              </li>
              <li>
                Deepgram and Cartesia process audio/text in real time and do not retain data after
                processing
              </li>
              <li>
                Twilio may retain call metadata (call SIDs, durations, phone numbers) per their own
                data retention policies
              </li>
              <li>
                All providers are bound by their respective privacy policies and data processing
                agreements
              </li>
            </ul>

            <h4 style={subheadingStyle}>4.2 Caregiver Access</h4>
            <p>
              If you are a <strong>loved one</strong>, your designated caregiver(s) can view:
            </p>
            <ul style={listStyle}>
              <li>Your medication schedules</li>
              <li>Call outcomes (whether you took your medications)</li>
              <li>Call history and summaries</li>
              <li>Your basic profile information</li>
            </ul>
            <p>
              Caregivers are granted access only through an explicit account setup or invitation
              process with defined permission levels.
            </p>

            <h4 style={subheadingStyle}>4.3 Legal Requirements</h4>
            <p>
              We may disclose your information if required by law, regulation, legal process, or
              governmental request, or to protect the rights, safety, or property of Little Sister,
              our users, or the public.
            </p>
          </Section>

          <Section title="5. Consent">
            <p>We obtain consent at multiple levels:</p>
            <h4 style={subheadingStyle}>5.1 Caregiver Consent</h4>
            <ul style={listStyle}>
              <li>
                <strong>SMS notifications:</strong> Opt-in consent collected during account setup
              </li>
              <li>
                <strong>Email notifications:</strong> Opt-in consent collected during account setup
              </li>
              <li>Consent status is recorded with timestamp and IP address in our audit system</li>
              <li>You can withdraw consent at any time through the Service settings</li>
            </ul>

            <h4 style={subheadingStyle}>5.2 Loved One Verbal Consent</h4>
            <ul style={listStyle}>
              <li>
                Before beginning regular medication reminder calls, we collect{' '}
                <strong>verbal consent</strong> from the loved one during an introductory call
              </li>
              <li>
                The AI assistant explains the Service and asks for explicit permission to continue
                making calls
              </li>
              <li>A transcript excerpt of the consent is stored as a record</li>
              <li>
                If consent is declined, no further reminder calls will be placed to that individual
              </li>
            </ul>

            <h4 style={subheadingStyle}>5.3 Withdrawing Consent</h4>
            <ul style={listStyle}>
              <li>
                Caregivers can withdraw SMS/email consent at any time through account settings
              </li>
              <li>Loved ones can verbally decline further calls during any reminder call</li>
              <li>Caregivers can pause or stop calls through the dashboard</li>
              <li>
                To fully withdraw all consent, contact us at{' '}
                <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                  wing@littlesister.ai
                </a>
              </li>
            </ul>
          </Section>

          <Section title="6. Data Retention">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Data Type</th>
                  <th style={thStyle}>Retention Period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>Account information</td>
                  <td style={tdStyle}>Retained until account deletion is requested</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Medication schedules</td>
                  <td style={tdStyle}>
                    Retained while active; historical changes preserved in snapshots
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Call transcripts and summaries</td>
                  <td style={tdStyle}>
                    Retained indefinitely for caregiver reference and audit purposes
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Consent records</td>
                  <td style={tdStyle}>Retained indefinitely (legal compliance requirement)</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Audit logs</td>
                  <td style={tdStyle}>
                    Retained indefinitely (security and compliance requirement)
                  </td>
                </tr>
                <tr>
                  <td style={tdStyle}>Caregiver invitations</td>
                  <td style={tdStyle}>Expire after 7 days; deleted after expiration</td>
                </tr>
              </tbody>
            </table>

            <h4 style={subheadingStyle}>Account Deletion</h4>
            <p>When you request account deletion:</p>
            <ul style={listStyle}>
              <li>
                All your personal data, medication records, call transcripts, consent records, and
                audit logs are permanently deleted
              </li>
              <li>Active medication reminders and scheduled calls are cancelled immediately</li>
              <li>Profile photos and any stored files are permanently removed</li>
              <li>This action is irreversible</li>
              <li>
                To request deletion, contact us at{' '}
                <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                  wing@littlesister.ai
                </a>
              </li>
            </ul>
          </Section>

          <Section title="7. Data Export">
            <p>
              You have the right to request a copy of the personal data we hold about you. To
              request a data export, contact us at{' '}
              <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                wing@littlesister.ai
              </a>
              . We will provide your data in a structured, machine-readable format within a
              reasonable timeframe.
            </p>
          </Section>

          <Section title="8. Data Security">
            <p>We implement the following security measures to protect your information:</p>
            <ul style={listStyle}>
              <li>
                <strong>Authentication:</strong> Industry-standard OAuth 2.0 via Auth0
              </li>
              <li>
                <strong>Encryption in transit:</strong> All data transmitted between your device,
                our servers, and third-party services is encrypted using TLS/SSL
              </li>
              <li>
                <strong>Access controls:</strong> Caregiver permission levels (primary/secondary)
                and access rights (view-only/full) limit who can see and modify data
              </li>
              <li>
                <strong>Audit logging:</strong> All data access and modifications are logged with
                timestamps and IP addresses
              </li>
            </ul>
            <p>
              While we take reasonable measures to protect your data, no method of electronic
              transmission or storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="9. Health Information Disclaimer">
            <p>
              Little Sister collects and processes medication-related information to provide
              reminder services.{' '}
              <strong>
                This information is used solely for the purpose of medication reminders and
                caregiver notifications.
              </strong>
            </p>
            <ul style={listStyle}>
              <li>We are not a healthcare provider, pharmacy, or medical device</li>
              <li>
                The medication information we store is provided by caregivers and is not verified
                against medical records
              </li>
              <li>We do not provide medical advice, diagnoses, or treatment recommendations</li>
              <li>
                You should consult qualified healthcare professionals for all medical decisions
              </li>
            </ul>
          </Section>

          <Section title="10. Automated Calls and AI Disclosure">
            <ul style={listStyle}>
              <li>
                All medication reminder calls and onboarding calls are conducted by an{' '}
                <strong>artificial intelligence assistant</strong>, not a human
              </li>
              <li>
                The AI assistant uses your loved one's name and medication information to
                personalize the conversation
              </li>
              <li>
                Call transcripts are generated automatically from speech-to-text processing and may
                contain errors
              </li>
              <li>
                AI-generated summaries are produced for caregiver convenience and may not capture
                every detail of a conversation
              </li>
            </ul>
          </Section>

          <Section title="11. Geographic Scope">
            <p>
              Little Sister is operated in and intended for users located in the United States. The
              Service is available in English and Traditional Chinese (繁體中文) to serve the
              linguistic needs of our US-based users. All data is processed and stored in the United
              States.
            </p>
          </Section>

          <Section title="12. Age Requirement">
            <p>
              Little Sister is not intended for use by individuals under the age of 18. Caregiver
              accounts may only be created by individuals who are at least 18 years old. We do not
              knowingly collect personal information from anyone under 18. If we learn that we have
              collected information from an individual under 18, we will take steps to delete that
              information promptly.
            </p>
          </Section>

          <Section title="13. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we
              will:
            </p>
            <ul style={listStyle}>
              <li>Update the "Last Updated" date at the top of this policy</li>
              <li>
                Notify registered caregivers via their preferred notification method (email or SMS)
              </li>
              <li>Post the updated policy on our website</li>
            </ul>
            <p>
              Your continued use of the Service after changes take effect constitutes acceptance of
              the revised policy.
            </p>
          </Section>

          <Section title="14. Contact Us">
            <p>
              If you have questions about this Privacy Policy, wish to exercise your data rights, or
              have concerns about how your information is handled, please contact us at{' '}
              <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                wing@littlesister.ai
              </a>
              .
            </p>
          </Section>

          <Section title="15. Your Rights">
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul style={listStyle}>
              <li>
                <strong>Access</strong> — Request a copy of the personal data we hold about you
              </li>
              <li>
                <strong>Correction</strong> — Request correction of inaccurate data
              </li>
              <li>
                <strong>Deletion</strong> — Request deletion of your data (subject to legal
                retention requirements)
              </li>
              <li>
                <strong>Data portability</strong> — Receive your data in a structured,
                machine-readable format
              </li>
              <li>
                <strong>Withdraw consent</strong> — Withdraw consent for notifications or reminder
                calls at any time
              </li>
              <li>
                <strong>Complaint</strong> — Lodge a complaint with a supervisory authority in your
                jurisdiction
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:wing@littlesister.ai" style={linkStyle}>
                wing@littlesister.ai
              </a>
              .
            </p>
          </Section>
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2
        style={{
          fontSize: '1.375rem',
          fontWeight: 600,
          color: '#1e3a5f',
          marginBottom: '1rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {title}
      </h2>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
          color: '#374151',
          lineHeight: 1.7,
        }}
      >
        {children}
      </div>
    </section>
  )
}

const subheadingStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#1e3a5f',
  marginTop: '0.5rem',
}

const listStyle: React.CSSProperties = {
  paddingLeft: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
}

const linkStyle: React.CSSProperties = {
  color: '#3b82f6',
  textDecoration: 'underline',
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.75rem',
  borderBottom: '2px solid #e5e7eb',
  color: '#1e3a5f',
  fontWeight: 600,
  backgroundColor: '#f9fafb',
}

const tdStyle: React.CSSProperties = {
  padding: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'top',
}
